import { useState } from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';

const QuickStartSection = styled.section`
  background: ${theme.colors.washi};
  border-radius: 24px;
  box-shadow: 
    0 4px 24px rgba(64, 145, 220, 0.05),
    0 1px 2px rgba(64, 145, 220, 0.03);
  padding: 20px;
`;

const Title = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${theme.colors.sumi};
  font-family: 'M PLUS Rounded 1c', sans-serif;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: '⚡️';
    font-size: 14px;
  }
`;

const TabContainer = styled.div``;

const TabList = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(64, 145, 220, 0.15);
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  
  &::-webkit-scrollbar {
    display: none;
  }
`;

const TabButton = styled.button<{ $active: boolean }>`
  background: ${({ $active }) => $active ? '#4091DC' : 'transparent'};
  color: ${({ $active }) => $active ? theme.colors.washi : theme.colors.sumi};
  border: 1px solid ${({ $active }) => $active ? 'transparent' : '#4091DC'};
  padding: 6px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-family: 'M PLUS Rounded 1c', sans-serif;
  font-size: 12px;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: ${({ $active }) => $active ? '#4091DC' : 'rgba(64, 145, 220, 0.1)'};
    transform: translateY(-1px);
  }
`;

const CodeBlock = styled.pre`
  background: rgba(64, 145, 220, 0.03);
  border: 1px solid rgba(64, 145, 220, 0.1);
  border-radius: 8px;
  padding: 16px;
  overflow-x: auto;
  font-family: 'Space Mono', monospace;
  font-size: 12px;
  line-height: 1.5;
  color: ${theme.colors.sumi};
  
  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(64, 145, 220, 0.2);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(64, 145, 220, 0.3);
  }
`;

const examples = {
  typescript: {
    title: 'Using fetch',
    code: `interface PulseContext {
  id: string;
  value: {
    memory: string;
    description: string;
    timestamp: string;
  };
}

async function getContext(agentId: string): Promise<PulseContext> {
  const response = await fetch(
    \`https://www.kosm-os.com/pulse/api/\${agentId}\`,
    {
      headers: {
        Authorization: \`Bearer \${YOUR_API_KEY}\`,
      },
    }
  );
  return response.json();
}`
  },
  nodejs: {
    title: 'Using axios',
    code: `const axios = require('axios');

const apiKey = 'YOUR_API_KEY';
const agentId = 'your_agent_id';

async function getContext() {
  try {
    const response = await axios.get(
      \`https://www.kosm-os.com/pulse/api/\${agentId}\`,
      {
        headers: {
          Authorization: \`Bearer \${apiKey}\`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error:', error);
  }
}`
  },
  python: {
    title: 'Using requests',
    code: `import requests

api_key = 'YOUR_API_KEY'
agent_id = 'your_agent_id'

def get_context():
    response = requests.get(
        f'https://www.kosm-os.com/pulse/api/{agent_id}',
        headers={'Authorization': f'Bearer {api_key}'}
    )
    return response.json()`
  }
};

type Language = keyof typeof examples;

export function QuickStart() {
  const [activeTab, setActiveTab] = useState<Language>('typescript');

  return (
    <QuickStartSection>
      <Title>Quick Start</Title>
      <TabContainer>
        <TabList>
          {(Object.keys(examples) as Language[]).map((lang) => (
            <TabButton
              key={lang}
              $active={activeTab === lang}
              onClick={() => setActiveTab(lang)}
            >
              {examples[lang].title}
            </TabButton>
          ))}
        </TabList>
        <CodeBlock>
          {examples[activeTab].code}
        </CodeBlock>
      </TabContainer>
    </QuickStartSection>
  );
} 